// Top-level app component.
//
// Replaces the Harmony shell:
//   - No /${appName} basename (root-relative URLs)
//   - AuthProvider wraps everything so useAuth() works inside any tool
//   - ProtectedRoute gates every tool route — visiting /image-studio without
//     a session redirects to Cognito's Hosted UI
//   - /auth/callback is the only unauthenticated route
//
// All tool components live under src/tools/ and shell pages under src/pages/.
// This is the single source of truth for the SmartBiz Toolkit UI — no more
// scattered harmony-migration/ folders across the workspace.

import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider.jsx';
import ProtectedRoute from './auth/ProtectedRoute.jsx';
import CallbackPage from './auth/CallbackPage.jsx';

// Shell pages
import Home from './pages/Home.jsx';
import AdminPage from './pages/AdminPage.jsx';

// Tool roots
import LeadEnrichment from './tools/lead-enrichment/Bliss.jsx';
import ListingCreation from './tools/listing-creation/ListingCreation.jsx';
import ImageLinkGenerator from './tools/image-link-generator/ImageLinkGenerator.jsx';
import ImageStudio from './tools/image-studio/ImageStudio.jsx';
import ProductImageGen from './tools/product-image-gen/ProductImageGen.jsx';
import StoreCROAudit from './tools/store-cro-audit/StoreCROAudit.jsx';
import AiChat from './tools/ai-chat/AiChat.jsx';
import KnowledgeAdmin from './tools/ai-chat/KnowledgeAdmin.jsx';
import DashboardPage from './tools/ai-chat/DashboardPage.jsx';
import AgentAuditReport from './tools/ai-chat/AgentAuditReport.jsx';

// Cloudscape global styles (otherwise components render unstyled)
import '@cloudscape-design/global-styles/index.css';
// Page-level styles ported from the Harmony shell — same dashboard look
import './pages/App.css';

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Cognito redirect target — rendered before auth resolves */}
          <Route path="/auth/callback" element={<CallbackPage />} />

          {/* Everything else requires auth */}
          <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
          <Route path="/lead-enrichment" element={<ProtectedRoute><LeadEnrichment /></ProtectedRoute>} />
          <Route path="/listing-creation" element={<ProtectedRoute><ListingCreation /></ProtectedRoute>} />
          <Route path="/image-link-generator" element={<ProtectedRoute><ImageLinkGenerator /></ProtectedRoute>} />
          <Route path="/image-studio" element={<ProtectedRoute><ImageStudio /></ProtectedRoute>} />
          <Route path="/product-image-gen" element={<ProtectedRoute><ProductImageGen /></ProtectedRoute>} />
          <Route path="/store-cro-audit" element={<ProtectedRoute adminOnly><StoreCROAudit /></ProtectedRoute>} />
          {/* AI Chat: opened up to amazon-internal (30 Jul 2026, Part 7 —
              matches the gate on /ai-chat/knowledge, which surfaces the
              same tool's learned facts and was already at this level).
              Was admin-only during initial untested rollout; now verified
              end-to-end multiple times. See ai-chat-tracker.md. */}
          <Route path="/ai-chat" element={<ProtectedRoute internalOnly><AiChat /></ProtectedRoute>} />
          {/* Knowledge base admin: view access widened to amazon-internal
              (any Amazon-internal user can see what the agent has
              learned); edit/delete stays admins-only, enforced both in
              KnowledgeAdmin.jsx's UI and server-side in the admin API
              routes. See ai-chat-tracker.md Part 5. */}
          <Route path="/ai-chat/knowledge" element={<ProtectedRoute internalOnly><KnowledgeAdmin /></ProtectedRoute>} />
          {/* Dashboards (4 Aug 2026) — same internalOnly gate as the rest
              of AI Chat (locked decision #5, ai-chat-dashboard-tracker.md).
              Opens in a new tab from the sidebar; view/edit permission
              (owner vs shared-with viewer) is enforced inside
              DashboardPage.jsx + server-side in dashboards-api. */}
          <Route path="/ai-chat/dashboard/:dashboardId" element={<ProtectedRoute internalOnly><DashboardPage /></ProtectedRoute>} />
          {/* Agent Audit Report (18 Aug 2026) — daily automated evaluation
              of AI Chat agent responses. Gated internalOnly so any
              Amazon-internal user can view quality metrics; transparency
              about agent quality is good for trust. */}
          <Route path="/ai-chat/agent-audit-report" element={<ProtectedRoute internalOnly><AgentAuditReport /></ProtectedRoute>} />

          {/* Anything else falls back to Home */}
          <Route path="*" element={<ProtectedRoute><Home /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
